import {
  getLeoUuid,
  LeoUUID,
  getBoolean,
  getObject,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { SurveyViewDetails, SurveyViewDetailsDTO } from "./surveyViewDetails";
import { SurveyDetailsAndDashboardViews, SurveyDetailsAndDashboardViewsDTO } from "./surveyDetailsAndDashboardViews";
/* eslint-disable import/export */
export abstract class CreateDashboardViewRPC implements
  LeoRPC<CreateDashboardViewRPC.Request, CreateDashboardViewRPC.Response, CreateDashboardViewRPC.Errors.Errors>
{
  abstract execute(
    request: CreateDashboardViewRPC.Request
  ): Promise<LeoRPCResult<CreateDashboardViewRPC.Response, CreateDashboardViewRPC.Errors.Errors>>;
}

export namespace CreateDashboardViewRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    surveyId: string;
    projectId: string;
    areAllQuestionsVisible: boolean;
    surveyViewDetails: SurveyViewDetailsDTO;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isCreateDashboardViewRPCRequest: boolean = true;
  
    readonly surveyId: LeoUUID;
    readonly projectId: LeoUUID;
    readonly areAllQuestionsVisible: boolean;
    readonly surveyViewDetails: SurveyViewDetails;
  
  
    constructor(
    surveyId: LeoUUID, 
    projectId: LeoUUID, 
    areAllQuestionsVisible: boolean, 
    surveyViewDetails: SurveyViewDetails
    ) {
      this.surveyId = surveyId;
      this.projectId = projectId;
      this.areAllQuestionsVisible = areAllQuestionsVisible;
      this.surveyViewDetails = surveyViewDetails;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const surveyId = getLeoUuid(dto, "surveyId", InvalidRequestSurveyIdError);
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const areAllQuestionsVisible = getBoolean(dto, "areAllQuestionsVisible", InvalidRequestAreAllQuestionsVisibleError);
      const surveyViewDetails = SurveyViewDetails.fromDTO(getObject(dto, "surveyViewDetails", InvalidRequestSurveyViewDetailsError));
      return new Request(
        surveyId,
        projectId,
        areAllQuestionsVisible,
        surveyViewDetails
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          surveyId: this.surveyId.toDTO(),
          projectId: this.projectId.toDTO(),
          areAllQuestionsVisible: this.areAllQuestionsVisible,
          surveyViewDetails: this.surveyViewDetails.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `CreateDashboardViewRPC(surveyId='${this.surveyId}',projectId='${this.projectId}',areAllQuestionsVisible='${this.areAllQuestionsVisible}',surveyViewDetails='${this.surveyViewDetails}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    surveyDetailsAndDashboardViews: SurveyDetailsAndDashboardViewsDTO;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isCreateDashboardViewResponse: boolean = true;
  
    readonly surveyDetailsAndDashboardViews: SurveyDetailsAndDashboardViews;
  
  
    constructor(
    surveyDetailsAndDashboardViews: SurveyDetailsAndDashboardViews
    ) {
      this.surveyDetailsAndDashboardViews = surveyDetailsAndDashboardViews;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const surveyDetailsAndDashboardViews = SurveyDetailsAndDashboardViews.fromDTO(getObject(dto, "surveyDetailsAndDashboardViews", InvalidResponseSurveyDetailsAndDashboardViewsError));
      return new Response(
        surveyDetailsAndDashboardViews
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          surveyDetailsAndDashboardViews: this.surveyDetailsAndDashboardViews.toDTO()
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `CreateDashboardView(surveyDetailsAndDashboardViews='${this.surveyDetailsAndDashboardViews}')`;
  };
  

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    InvalidSurveyId = "INVALID_SURVEY_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
    SurveyViewAlreadyExists = "SURVEY_VIEW_ALREADY_EXISTS",
    NoSurveyLinkFound = "NO_SURVEY_LINK_FOUND",
    MaximumSurveyViewsReached = "MAXIMUM_SURVEY_VIEWS_REACHED"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `CreateDashboardViewRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface InvalidSurveyIdDTO {
      code: string;
    }
    
    export class InvalidSurveyId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyIdDTO {
        const dto: InvalidSurveyIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyId.prototype.toString = function toString(): string {
      return `CreateDashboardViewRPC.INVALID_SURVEY_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `CreateDashboardViewRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    
    
    export interface SurveyViewAlreadyExistsDTO {
      code: string;
    }
    
    export class SurveyViewAlreadyExists extends LeoRPCError<RPCError> {
      readonly isSurveyViewAlreadyExists: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.SurveyViewAlreadyExists;
        super(code);
      }
    
    
      toDTO(): SurveyViewAlreadyExistsDTO {
        const dto: SurveyViewAlreadyExistsDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    SurveyViewAlreadyExists.prototype.toString = function toString(): string {
      return `CreateDashboardViewRPC.SURVEY_VIEW_ALREADY_EXISTS()`;
    };
    
    
    export interface NoSurveyLinkFoundDTO {
      code: string;
    }
    
    export class NoSurveyLinkFound extends LeoRPCError<RPCError> {
      readonly isNoSurveyLinkFound: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.NoSurveyLinkFound;
        super(code);
      }
    
    
      toDTO(): NoSurveyLinkFoundDTO {
        const dto: NoSurveyLinkFoundDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    NoSurveyLinkFound.prototype.toString = function toString(): string {
      return `CreateDashboardViewRPC.NO_SURVEY_LINK_FOUND()`;
    };
    
    
    export interface MaximumSurveyViewsReachedDTO {
      code: string;
    }
    
    export class MaximumSurveyViewsReached extends LeoRPCError<RPCError> {
      readonly isMaximumSurveyViewsReached: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.MaximumSurveyViewsReached;
        super(code);
      }
    
    
      toDTO(): MaximumSurveyViewsReachedDTO {
        const dto: MaximumSurveyViewsReachedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    MaximumSurveyViewsReached.prototype.toString = function toString(): string {
      return `CreateDashboardViewRPC.MAXIMUM_SURVEY_VIEWS_REACHED()`;
    };
    

    export type Errors = InvalidProjectId | InvalidSurveyId | ProjectAlreadyArchived | SurveyViewAlreadyExists | NoSurveyLinkFound | MaximumSurveyViewsReached;
    export type ErrorsDTO = InvalidProjectIdDTO | InvalidSurveyIdDTO | ProjectAlreadyArchivedDTO | SurveyViewAlreadyExistsDTO | NoSurveyLinkFoundDTO | MaximumSurveyViewsReachedDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.InvalidSurveyId:
          return new InvalidSurveyId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        case RPCError.SurveyViewAlreadyExists:
          return new SurveyViewAlreadyExists();
        case RPCError.NoSurveyLinkFound:
          return new NoSurveyLinkFound();
        case RPCError.MaximumSurveyViewsReached:
          return new MaximumSurveyViewsReached();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestSurveyIdError extends CreateDashboardViewRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyIdError";
  }
}

export class InvalidRequestProjectIdError extends CreateDashboardViewRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestAreAllQuestionsVisibleError extends CreateDashboardViewRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestAreAllQuestionsVisibleError";
  }
}

export class InvalidRequestSurveyViewDetailsError extends CreateDashboardViewRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyViewDetailsError";
  }
}

export class InvalidResponseSurveyDetailsAndDashboardViewsError extends CreateDashboardViewRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyDetailsAndDashboardViewsError";
  }
}

export class InvalidResponseInvalidProjectIdError extends CreateDashboardViewRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseInvalidSurveyIdError extends CreateDashboardViewRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends CreateDashboardViewRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

export class InvalidResponseSurveyViewAlreadyExistsError extends CreateDashboardViewRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyViewAlreadyExistsError";
  }
}

export class InvalidResponseNoSurveyLinkFoundError extends CreateDashboardViewRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseNoSurveyLinkFoundError";
  }
}

export class InvalidResponseMaximumSurveyViewsReachedError extends CreateDashboardViewRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseMaximumSurveyViewsReachedError";
  }
}

